import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth, useToast } from "../App";
import { api } from "../lib/api";
import type { Plan } from "../lib/types";

interface Tier {
  plan: Plan;
  name: string;
  tagline: string;
  price: string;
}

// Display copy only - the actual charge comes from whatever Price is behind
// LEMON_SQUEEZY_VARIANT_PREMIUM / LEMON_SQUEEZY_VARIANT_PRO in .env (set in the Lemon Squeezy
// Dashboard). Keep these two numbers in sync with that by hand; nothing
// wires them together automatically.
const TIERS: Tier[] = [
  { plan: "free", name: "Free", tagline: "Try it on one real upload.", price: "$0" },
  {
    plan: "premium",
    name: "Premium",
    tagline: "A full course load, more decks and cards per run.",
    price: "$5/mo",
  },
  {
    plan: "pro",
    name: "Pro",
    tagline: "AI chat refinement, on top of Premium.",
    price: "$15/mo",
  },
];

// The tier order, so "is this an upgrade or a downgrade" is a comparison,
// not a special case per plan.
const RANK: Record<Plan, number> = { free: 0, premium: 1, pro: 2 };

const ROWS: { label: string; free: string; premium: string; pro: string }[] = [
  { label: "Decks", free: "3", premium: "10", pro: "Unlimited" },
  { label: "Cards per generation run", free: "Up to 25", premium: "Up to 150", pro: "Up to 500" },
  { label: "Upload size", free: "2 MB", premium: "50 MB", pro: "200 MB" },
  { label: "Uploads per day", free: "1", premium: "25", pro: "Unlimited" },
  { label: "AI chat refinement", free: "—", premium: "—", pro: "✓" },
  { label: "Image occlusion, cloze, review, export", free: "✓", premium: "✓", pro: "✓" },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const { push, pushError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [busyPlan, setBusyPlan] = useState<Plan | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  // Checkout redirects the browser back here with ?checkout=success|cancelled
  // rather than through the API, so this is the one place that result can be
  // shown - the webhook has usually already flipped the plan by the time the
  // browser gets back, but refresh() picks it up either way.
  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (!checkout) return;
    if (checkout === "success") {
      push("Subscription active. Thanks!", "good");
      refresh();
    } else if (checkout === "cancelled") {
      push("Checkout cancelled - no charge was made.", "info");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });
    // Only ever meant to run once, right after checkout redirects back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgrade = async (plan: Plan) => {
    if (plan !== "premium" && plan !== "pro") return;
    setBusyPlan(plan);
    try {
      const { url } = await api.checkout(plan);
      window.location.href = url;
    } catch (error) {
      pushError(error);
      setBusyPlan(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { url } = await api.billingPortal();
      window.location.href = url;
    } catch (error) {
      pushError(error);
      setPortalBusy(false);
    }
  };

  return (
    <main className="page page-narrow">
      <div className="page-head">
        <div className="grow">
          <h1>Plans</h1>
          <p>Start free. Upgrade when a real course load needs more.</p>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        {TIERS.map((tier) => {
          const isCurrent = user?.plan === tier.plan;
          const isUpgrade = tier.plan !== "free" && (!user || RANK[user.plan] < RANK[tier.plan]);

          return (
            <div
              key={tier.plan}
              className="panel"
              style={tier.plan === "pro" ? { borderColor: "var(--accent)" } : undefined}
            >
              <div className="row">
                <h2>{tier.name}</h2>
                {isCurrent && (
                  <span className="chip chip-good" style={{ marginLeft: "auto" }}>
                    Your plan
                  </span>
                )}
              </div>
              <p className="muted small">{tier.tagline}</p>
              <div style={{ fontSize: 26, fontWeight: 650, margin: "10px 0" }}>{tier.price}</div>

              {!user ? (
                tier.plan !== "free" ? (
                  <Link to="/" className="btn btn-primary">
                    Sign up to subscribe
                  </Link>
                ) : (
                  <Link to="/" className="btn">
                    Get started
                  </Link>
                )
              ) : isCurrent ? (
                tier.plan !== "free" ? (
                  <button className="btn" onClick={openPortal} disabled={portalBusy}>
                    {portalBusy ? "Opening…" : "Manage billing"}
                  </button>
                ) : null
              ) : isUpgrade ? (
                <button
                  className="btn btn-primary"
                  onClick={() => upgrade(tier.plan)}
                  disabled={busyPlan !== null}
                >
                  {busyPlan === tier.plan ? "Redirecting…" : `Upgrade to ${tier.name}`}
                </button>
              ) : (
                // Already on a higher tier than this one - downgrading
                // happens through the portal, not by "buying" a lower plan.
                <button className="btn" onClick={openPortal} disabled={portalBusy}>
                  Manage billing
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="panel scroll-x" style={{ marginTop: 20 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
              <th style={{ padding: "8px 6px" }}></th>
              <th style={{ padding: "8px 6px" }}>Free</th>
              <th style={{ padding: "8px 6px" }}>Premium</th>
              <th style={{ padding: "8px 6px" }}>Pro</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.label} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: "8px 6px" }}>{row.label}</td>
                <td style={{ padding: "8px 6px" }} className="muted">
                  {row.free}
                </td>
                <td style={{ padding: "8px 6px" }} className="muted">
                  {row.premium}
                </td>
                <td style={{ padding: "8px 6px" }} className="muted">
                  {row.pro}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
